// Dashboard treatment quick-log modal (TREATMENT_LOGGING_PLAN.md Phase 2). Lets the
// user log a non-medication treatment/remedy — and rate how well recent ones worked —
// without going through chat (no Bedrock). Modeled structurally on QuickMedLog.tsx.
//
// Flow: pick → detail → (save closes the modal; parent shows an undo toast).
//  - pick: unrated treatments from the last 48h surface at top for one-tap rating;
//    below, recent distinct treatments (last 90 days, ranked by frequency) and a
//    grid of category tiles for a fresh log.
//  - detail: name/duration/symptom-link/effect/note, direct-saved via save-batch.
//    Save closes the modal immediately (matches QuickMedLog); if you didn't rate
//    it here, it resurfaces in "How did these work?" next time you open this modal.

import { useEffect, useMemo, useState } from "react";
import type { MedicalEvent, TreatmentCategory, TreatmentEffect } from "@medassist/shared";
import {
  TREATMENT_CATEGORIES,
  TREATMENT_CATEGORY_LABELS,
  TREATMENT_EFFECTS,
  TREATMENT_EFFECT_LABELS,
  symptomFollowUpKey,
} from "@medassist/shared";
import {
  listEvents,
  saveBatchEvents,
  patchTreatmentEffect,
  ApiError,
  type BatchSaveRef,
} from "../lib/api.js";
import { submitFeedback } from "../lib/api.js";
import { ActivityIcon } from "./icons.js";
import { ModalShell } from "./ModalShell.js";
import { ClearableInput, ClearableTextarea } from "./ClearableInput.js";
import { FeedbackThumbs } from "./FeedbackThumbs.js";

type Step = "pick" | "detail";

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;
const MAX_RECENT = 12;
const MAX_SYMPTOM_CHOICES = 12;

function todayStr() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

interface RecentTreatment {
  name: string;
  category?: TreatmentCategory;
  count: number;
  lastUsedAt: string;
}

interface SymptomChip {
  key: string;
  name: string;
  count: number;
}

interface DetailSeed {
  name: string;
  category?: TreatmentCategory;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const hrs = Math.round(ms / (60 * 60 * 1000));
  if (hrs < 1) return "just now";
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function buildRecentTreatments(events: MedicalEvent[]): RecentTreatment[] {
  const cutoff = Date.now() - NINETY_DAYS_MS;
  const groups = new Map<string, RecentTreatment>();

  const sorted = events
    .filter((e) => !e.deleted && Date.parse(e.occurredAt) >= cutoff)
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  for (const e of sorted) {
    const p = e.payload as Record<string, unknown>;
    const name = typeof p.name === "string" ? p.name.trim() : "";
    if (!name) continue;
    const key = name.toLowerCase().trim();
    const category = typeof p.category === "string" ? (p.category as TreatmentCategory) : undefined;
    const g = groups.get(key);
    if (g) {
      g.count++;
      g.lastUsedAt = e.occurredAt;
      if (category) g.category = category;
    } else {
      groups.set(key, { name, category, count: 1, lastUsedAt: e.occurredAt });
    }
  }

  return [...groups.values()].sort((a, b) => b.count - a.count);
}

function buildSymptomChips(symptomEvents: MedicalEvent[], headacheEvents: MedicalEvent[]): SymptomChip[] {
  const cutoff = Date.now() - NINETY_DAYS_MS;
  const groups = new Map<string, SymptomChip>();

  const allEvents = [...symptomEvents, ...headacheEvents].filter(
    (e) => !e.deleted && Date.parse(e.occurredAt) >= cutoff,
  );

  for (const e of allEvents) {
    const p = e.payload as Record<string, unknown>;
    let key: string, name: string;
    if (e.eventType === "symptom") {
      name = typeof p.name === "string" ? p.name.trim() : "";
      if (!name) continue;
      key = symptomFollowUpKey(name);
    } else {
      key = symptomFollowUpKey("headache");
      name = "headache";
    }
    const g = groups.get(key);
    if (g) g.count++;
    else groups.set(key, { key, name, count: 1 });
  }

  return [...groups.values()].sort((a, b) => b.count - a.count).slice(0, MAX_SYMPTOM_CHOICES);
}

function EffectButtons({
  onPick,
  disabled,
}: {
  onPick: (effect: TreatmentEffect) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {TREATMENT_EFFECTS.map((eff) => (
        <button
          key={eff}
          type="button"
          disabled={disabled}
          onClick={() => onPick(eff)}
          className="rounded-lg border border-gray-200 dark:border-gray-800 px-2 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {TREATMENT_EFFECT_LABELS[eff]}
        </button>
      ))}
    </div>
  );
}

export function QuickTreatmentLog({
  patientId,
  onClose,
  onLogged,
  onGoToChat,
}: {
  patientId: string;
  onClose: () => void;
  /** Called after a successful save so the parent can toast + refresh; the modal
   *  closes itself right after (matches QuickMedLog's pattern). */
  onLogged: (ref: BatchSaveRef, label: string) => void;
  onGoToChat: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const [unrated, setUnrated] = useState<MedicalEvent[]>([]);
  const [recent, setRecent] = useState<RecentTreatment[]>([]);
  const [symptomChips, setSymptomChips] = useState<SymptomChip[]>([]);

  const [step, setStep] = useState<Step>("pick");
  const [seed, setSeed] = useState<DetailSeed | null>(null);

  const [name, setName] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [category, setCategory] = useState<TreatmentCategory | undefined>(undefined);
  const [forSymptom, setForSymptom] = useState<SymptomChip | null>(null);
  const [effect, setEffect] = useState<TreatmentEffect | undefined>(undefined);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [timeMode, setTimeMode] = useState<"now" | "custom">("now");
  const [customDate, setCustomDate] = useState(todayStr);
  const [customTime, setCustomTime] = useState(() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
  });

  function selectCustomMode() {
    const n = new Date();
    setCustomTime(`${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`);
    setCustomDate(todayStr());
    setTimeMode("custom");
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [treatmentEvents, symptomEvents, headacheEvents] = await Promise.all([
          listEvents(patientId, "treatment"),
          listEvents(patientId, "symptom"),
          listEvents(patientId, "headache"),
        ]);
        if (cancelled) return;
        const cutoff = Date.now() - FORTY_EIGHT_HOURS_MS;
        setUnrated(
          treatmentEvents
            .filter((e) => !e.deleted && Date.parse(e.occurredAt) >= cutoff)
            .filter((e) => !(e.payload as Record<string, unknown>).effect)
            .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt)),
        );
        setRecent(buildRecentTreatments(treatmentEvents));
        setSymptomChips(buildSymptomChips(symptomEvents, headacheEvents));
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : "Couldn't load recent treatments");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  const filteredRecent = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recent.slice(0, MAX_RECENT);
    return recent.filter((r) => r.name.toLowerCase().includes(q));
  }, [recent, query]);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TREATMENT_CATEGORIES;
    return TREATMENT_CATEGORIES.filter((c) => TREATMENT_CATEGORY_LABELS[c].toLowerCase().includes(q));
  }, [query]);

  async function rateUnrated(e: MedicalEvent, eff: TreatmentEffect) {
    const prev = unrated;
    setUnrated((cur) => cur.filter((x) => x.eventId !== e.eventId));
    try {
      await patchTreatmentEffect(patientId, e.eventId, e.occurredAt, eff);
    } catch (err) {
      setUnrated(prev);
      setError(err instanceof ApiError ? err.message : "Couldn't save that rating");
    }
  }

  async function handleListFeedback(
    rating: "up" | "down",
    opts: { reasonText?: string },
  ) {
    await submitFeedback({
      patientId,
      sessionId: "quick-treatment-log",
      mode: "capture",
      rating,
      sourceSurface: "quick_treatment_log",
      ...(rating === "down"
        ? { reasonCategory: "inaccurate" as const, reasonText: opts.reasonText }
        : {}),
      pendingProposal: {
        kind: "quick_treatment_log_list",
        patientId,
        query: query.trim() || undefined,
        unrated: unrated.map((e) => ({
          eventId: e.eventId,
          name: (e.payload as Record<string, unknown>).name,
          occurredAt: e.occurredAt,
        })),
        recent: filteredRecent.map((r) => ({ name: r.name, count: r.count })),
      },
    });
  }

  function openDetail(s: DetailSeed) {
    setSeed(s);
    setName(s.name);
    setCategory(s.category);
    setDurationMin("");
    setForSymptom(null);
    setEffect(undefined);
    setNote("");
    setShowNote(false);
    setTimeMode("now");
    setCustomDate(todayStr());
    setError(null);
    setStep("detail");
  }

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a treatment name");
      return;
    }
    let startedAt = new Date().toISOString();
    if (timeMode === "custom" && customTime) {
      const [year, month, day] = customDate.split("-").map(Number);
      const [h, m] = customTime.split(":").map(Number);
      startedAt = new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0).toISOString();
    }
    setSaving(true);
    setError(null);
    try {
      const duration = Number(durationMin);
      const payload: Record<string, unknown> = {
        name: trimmed,
        startedAt,
        ...(category ? { category } : {}),
        ...(durationMin.trim() && duration > 0 ? { durationMin: duration } : {}),
        ...(forSymptom ? { forSymptomName: forSymptom.name } : {}),
        ...(effect ? { effect } : {}),
        ...(note.trim() ? { notes: note.trim() } : {}),
      };
      const [saved] = await saveBatchEvents(patientId, [{ eventType: "treatment", payload }]);
      if (!saved) throw new Error("Save failed");
      const displayName = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
      onLogged(saved, `Logged ${displayName}`);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that treatment");
      setSaving(false);
    }
  }

  const title = step === "pick" ? "Log a treatment" : seed?.name || "New treatment";

  return (
    <ModalShell
      title={title}
      icon={<ActivityIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      subheader={
        step === "pick" && !loading ? (
          <ClearableInput
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search treatments…"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
        ) : undefined
      }
      bodyClassName={"overflow-y-auto p-4" + (step === "pick" ? " h-[60vh]" : "")}
    >
      {error && (
        <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}

      {/* ── Step: pick ─────────────────────────────────────────────── */}
      {step === "pick" && (
        <>
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading recent treatments…</p>
          ) : (
            <div className="space-y-4">
              {unrated.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    How did these work?
                  </p>
                  <ul className="space-y-2">
                    {unrated.map((e) => {
                      const p = e.payload as Record<string, unknown>;
                      const nm = typeof p.name === "string" ? p.name : "Treatment";
                      return (
                        <li
                          key={e.eventId}
                          className="rounded-lg border border-gray-200 dark:border-gray-800 p-2"
                        >
                          <div className="mb-1.5 flex items-center gap-2 text-sm">
                            <span className="flex-1 font-medium text-gray-900 dark:text-gray-100">{nm}</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {relativeTime(e.occurredAt)}
                            </span>
                          </div>
                          <EffectButtons onPick={(eff) => void rateUnrated(e, eff)} />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div>
                {filteredRecent.length > 0 && (
                  <>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Recent treatments — tap to log again.
                    </p>
                    <ul className="mb-3 space-y-1">
                      {filteredRecent.map((r) => {
                        const displayName = r.name.charAt(0).toUpperCase() + r.name.slice(1);
                        return (
                          <li key={r.name.toLowerCase()}>
                            <button
                              onClick={() => openDetail({ name: r.name, category: r.category })}
                              className="flex w-full items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <span className="flex-1 font-medium text-gray-900 dark:text-gray-100">
                                {displayName}
                              </span>
                              {r.category && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  {TREATMENT_CATEGORY_LABELS[r.category]}
                                </span>
                              )}
                              <span className="rounded-full bg-teal-100 dark:bg-teal-900/50 px-2 py-0.5 text-xs font-medium text-teal-700 dark:text-teal-300">
                                {r.count}×
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}

                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Or start a new one:</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {filteredCategories.map((c) => (
                    <button
                      key={c}
                      onClick={() => openDetail({ name: "", category: c })}
                      className="rounded-lg border border-gray-200 dark:border-gray-800 px-2 py-2 text-center text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      {TREATMENT_CATEGORY_LABELS[c]}
                    </button>
                  ))}
                </div>
                {filteredRecent.length === 0 && filteredCategories.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No matches.</p>
                )}
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
                <button
                  onClick={() => { onClose(); onGoToChat(); }}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Log via chat instead
                </button>
              </div>

              <FeedbackThumbs
                prompt="Anything wrong with this list?"
                placeholder="e.g. a treatment you logged isn't showing, or search isn't matching it (optional)"
                onSubmit={handleListFeedback}
              />
            </div>
          )}
        </>
      )}

      {/* ── Step: detail ───────────────────────────────────────────── */}
      {step === "detail" && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Treatment</label>
            <ClearableInput
              type="text"
              autoFocus={!seed?.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={category ? TREATMENT_CATEGORY_LABELS[category] : "e.g. ice pack on knee"}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Duration (minutes, optional)
            </label>
            <input
              type="number"
              min={0}
              value={durationMin}
              onChange={(e) => setDurationMin(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Time</label>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex rounded-full bg-gray-100 dark:bg-gray-800 p-0.5 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setTimeMode("now")}
                  className={
                    "rounded-full px-4 py-1 transition-all " +
                    (timeMode === "now"
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400")
                  }
                >
                  Now
                </button>
                <button
                  type="button"
                  onClick={selectCustomMode}
                  className={
                    "rounded-full px-4 py-1 transition-all " +
                    (timeMode === "custom"
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400")
                  }
                >
                  Custom
                </button>
              </div>
              {timeMode === "custom" && (
                <>
                  <input
                    type="date"
                    value={customDate}
                    max={todayStr()}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                  <input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                </>
              )}
            </div>
          </div>

          {symptomChips.length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                For symptom (optional)
              </label>
              <div className="flex flex-wrap gap-1.5">
                {symptomChips.map((c) => {
                  const active = forSymptom?.key === c.key;
                  const displayName = c.name.charAt(0).toUpperCase() + c.name.slice(1);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setForSymptom(active ? null : c)}
                      className={
                        "rounded-full border px-3 py-1 text-xs font-medium " +
                        (active
                          ? "border-(--accent) bg-(--accent) text-(--accent-contrast)"
                          : "border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800")
                      }
                    >
                      {displayName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Did it help? (optional — you can rate later)
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {TREATMENT_EFFECTS.map((eff) => (
                <button
                  key={eff}
                  type="button"
                  onClick={() => setEffect(effect === eff ? undefined : eff)}
                  className={
                    "rounded-lg border px-2 py-1.5 text-xs font-medium " +
                    (effect === eff
                      ? "border-(--accent) bg-(--accent) text-(--accent-contrast)"
                      : "border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800")
                  }
                >
                  {TREATMENT_EFFECT_LABELS[eff]}
                </button>
              ))}
            </div>
          </div>

          {showNote ? (
            <ClearableTextarea
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note (optional)…"
              rows={2}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
          ) : (
            <button
              onClick={() => setShowNote(true)}
              className="text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
            >
              + Add a note
            </button>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => setStep("pick")}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Back
            </button>
            <button
              onClick={() => void save()}
              disabled={saving}
              className="flex-1 rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
